import { questionnaireTypes } from "@/components/questionnaire/questionnaire.config.ts";
import {
  getFieldWithQuestionnaireSlug,
  getWriteToFieldEffectiveFromSlug,
  getWriteToFieldFields,
  getWriteToFieldRowsKey,
  getWriteToFieldTable,
} from "@/components/questionnaire/components/write-to-field-element.utils.ts";
import {
  getFormKey,
  QuestionnaireFormValueTypes,
  QuestionnaireType,
} from "@/components/questionnaire/questionnaire.types.ts";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { useRecent } from "@/hooks/use-recent.hook.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiCustomFieldValue,
  ApiField,
  ApiFieldTypes,
  ApiTableTypes,
} from "@/types/custom-field.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { ApiFormViewPage } from "@/types/forms/form-view.types.ts";
import {
  ApiFormElementWriteToFieldTableMode,
  ApiFormElementWriteToFieldTargetType,
} from "@/types/forms/form-edit.types.ts";
import {
  ApiFormElementType,
  ApiFormResultAnswer,
  ApiFormResultCompensationLine,
  ApiFormType,
} from "@/types/forms/form.types.ts";
import { ApiRecentKey } from "@/types/recents/recents.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo, useRef, useState } from "react";
import { FieldErrors, useForm, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

export const normalizeCompensationLinesForSubmit = (
  lines: ApiFormResultCompensationLine[]
): ApiFormResultCompensationLine[] =>
  lines.map((line, index) => ({
    ...line,
    id: line.id || null,
    sort: index,
    amount_gross:
      line.amount_gross === "" || line.amount_gross == null ? null : Number(line.amount_gross),
    amount_net: line.amount_net === "" || line.amount_net == null ? null : Number(line.amount_net),
    percent: line.percent === "" || line.percent == null ? null : Number(line.percent),
  }));

export const getTargetEmployeeFieldValue = (
  field: ApiField,
  targetEmployee: ApiEmployee | undefined,
  organizationCurrency?: string
): ApiCustomFieldValue | undefined => {
  if (!targetEmployee) return undefined;

  const position = targetEmployee.employee_position;
  const employment = targetEmployee.employee_employment;
  const compensation = targetEmployee.employee_compensation;

  switch (field.type) {
    case ApiFieldTypes.employee_position_department:
      return position?.department?.id ? { value_int_array: [position.department.id] } : undefined;
    case ApiFieldTypes.employee_position_division:
      return position?.division?.id ? { value_int_array: [position.division.id] } : undefined;
    case ApiFieldTypes.employee_position_job_profile:
      return position?.job_profile?.id ? { value_int_array: [position.job_profile.id] } : undefined;
    case ApiFieldTypes.employee_position_reporting_to:
      return position?.manager?.id ? { value_int_array: [position.manager.id] } : undefined;
    case ApiFieldTypes.employee_position_location:
      return position?.location?.id ? { value_int_array: [position.location.id] } : undefined;
    case ApiFieldTypes.employee_position_change_reason:
      return position?.change_reason?.id
        ? { value_int_array: [position.change_reason.id] }
        : undefined;
    case ApiFieldTypes.employee_position_comment:
      return position?.comment ? { value_text: position.comment } : undefined;
    case ApiFieldTypes.employee_employment_type:
      return employment?.employment_type?.id
        ? { value_int_array: [employment.employment_type.id] }
        : undefined;
    case ApiFieldTypes.employee_employment_contract:
      return employment?.employment_contract?.id
        ? { value_int_array: [employment.employment_contract.id] }
        : undefined;
    case ApiFieldTypes.employee_employment_work_pattern:
      return employment?.work_pattern?.id
        ? { value_int_array: [employment.work_pattern.id] }
        : undefined;
    case ApiFieldTypes.employee_employment_legal_entity:
      return employment?.legal_entity?.id
        ? { value_int_array: [employment.legal_entity.id] }
        : undefined;
    case ApiFieldTypes.employee_employment_probation_date_end:
      return employment?.date_probation_ends
        ? { date_value: employment.date_probation_ends }
        : undefined;
    case ApiFieldTypes.employee_employment_comment:
      return employment?.comment ? { value_text: employment.comment } : undefined;
    case ApiFieldTypes.employee_compensation_base_salary_gross:
      return compensation?.base_salary_gross
        ? { value_int: Number(compensation.base_salary_gross) }
        : undefined;
    case ApiFieldTypes.employee_compensation_base_salary_net:
      return compensation?.base_salary_net
        ? { value_int: Number(compensation.base_salary_net) }
        : undefined;
    case ApiFieldTypes.employee_compensation_currency:
      return compensation?.currency ? { value_text_array: [compensation.currency] } : undefined;
    case ApiFieldTypes.employee_compensation_per_type:
      return compensation?.per_type ? { value_text_array: [compensation.per_type] } : undefined;
    case ApiFieldTypes.employee_compensation_change_reason:
      return compensation?.change_reason?.id
        ? { value_int_array: [compensation.change_reason.id] }
        : undefined;
    case ApiFieldTypes.employee_compensation_comment:
      return compensation?.comment ? { value_text: compensation.comment } : undefined;
    case ApiFieldTypes.employee_additional_compensation_currency:
      return organizationCurrency ? { value_text_array: [organizationCurrency] } : undefined;
    default:
      return undefined;
  }
};

interface UseQuestionnaireReturn {
  methods: UseFormReturn<QuestionnaireFormValueTypes>;
  questionnaireForm: ReturnType<QuestionnaireType["hooks"]["useGetQuestionnaireForm"]>["data"];
  currentPage: number;
  totalPages: number;
  isSaving: boolean;
  isSubmitting: boolean;
  submitQuestionnaire: () => void;
  handleNextPage: () => void;
  handlePreviousPage: () => void;
  handleSaveDraft: () => Promise<void>;
  questionnaireType: QuestionnaireType;
  errors: FieldErrors<Record<string, string | string[]>>;
}

export const useQuestionnaire = (
  type: ApiFormType,
  id: number | string | undefined,
  onAfterSubmit?: () => void,
  targetEmployee?: ApiEmployee
): UseQuestionnaireReturn => {
  const { data: employeeMe } = useEmployeeMe();
  const { getSchemaForField, getValueForFieldFromValueObject, transformFormDataToValue } =
    useCustomFieldsHook();
  const { addRecent } = useRecent();

  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [isDocumentVisible, setIsDocumentVisible] = useState<boolean>(true);
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [isSaving, setIsSaving] = useState(false);

  const questionnaireType = questionnaireTypes[type];

  const { data: questionnaireForm } = questionnaireType.hooks.useGetQuestionnaireForm({
    employeeId: employeeMe?.id || 0,
    questionnaireId: Number(id),
    enabled: !!employeeMe,
  });

  const submitQuestionnaire = questionnaireType.hooks.useSubmitQuestionnaire();
  const saveDraft = questionnaireType.hooks.useSaveDraft();

  const getSingleFieldValue = (
    field: ApiField,
    formData: QuestionnaireFormValueTypes
  ): QuestionnaireFormValueTypes[string] => {
    const fieldValue = formData[field.slug];

    if (!Array.isArray(fieldValue)) return fieldValue;
    const firstValue = fieldValue[0];
    return typeof firstValue === "number" || typeof firstValue === "string"
      ? firstValue
      : undefined;
  };

  const transformWriteToFieldFormDataToValue = (
    field: ApiField,
    formData: QuestionnaireFormValueTypes
  ): ApiCustomFieldValue => {
    switch (field.type) {
      case ApiFieldTypes.employee_gender: {
        const genderValue = getSingleFieldValue(field, formData);

        return {
          value_text: typeof genderValue === "string" ? genderValue : undefined,
        };
      }

      case ApiFieldTypes.employee_address_country: {
        const countryValue = getSingleFieldValue(field, formData);
        const countryId =
          typeof countryValue === "number"
            ? countryValue
            : typeof countryValue === "string"
              ? Number(countryValue)
              : undefined;

        return {
          value_int: Number.isFinite(countryId) ? countryId : undefined,
        };
      }

      default:
        return transformFormDataToValue(field, formData);
    }
  };

  const isFormValueFilled = (value: unknown): boolean => {
    if (value == null) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (value instanceof Date) return !Number.isNaN(value.getTime());
    if (typeof value === "string") return value.trim().length > 0;
    if (typeof value === "boolean") return true;

    return true;
  };

  const hasFilledWriteToFieldValue = (
    formData: QuestionnaireFormValueTypes,
    fieldSlugs: string[]
  ): boolean => {
    return fieldSlugs.some((fieldSlug) => isFormValueFilled(formData[fieldSlug]));
  };

  const getWriteToFieldValidationSlugs = (fields: ApiField[]): string[] => {
    return fields.flatMap((field) => [field.slug, `${field.slug}_currency`]);
  };

  const getWriteToFieldSchemaForField = (
    field: ApiField,
    isRequired: boolean,
    requiredWhenFilledSlugs: string[] = []
  ): Record<string, yup.AnySchema> => {
    const requiredSchema = getSchemaForField({ ...field, is_mandatory: true });
    const optionalSchema = getSchemaForField({ ...field, is_mandatory: false });

    if (isRequired) return requiredSchema;
    if (!field.is_mandatory) return optionalSchema;

    return Object.keys(requiredSchema).reduce<Record<string, yup.AnySchema>>(
      (result, schemaKey) => {
        result[schemaKey] = yup.lazy((_, options) => {
          const formData = (options.parent ?? {}) as QuestionnaireFormValueTypes;

          return hasFilledWriteToFieldValue(formData, requiredWhenFilledSlugs)
            ? requiredSchema[schemaKey]
            : optionalSchema[schemaKey];
        }) as unknown as yup.AnySchema;

        return result;
      },
      {}
    );
  };

  const getCompensationLinesSchema = (isRequired: boolean) => {
    const schema = yup
      .array()
      .of(
        yup.object({
          compensation_component_id: yup.number().moreThan(0).required(t("errors:field_required")),
          amount_gross: yup.mixed().nullable(),
          amount_net: yup.mixed().nullable(),
          currency_code: yup.string().nullable(),
          per_type: yup.string().nullable(),
          rate_type: yup.string().nullable(),
          percent: yup.mixed().nullable(),
        })
      )
      .test("valid-compensation-lines", t("errors:field_required"), (lines) => {
        if (!lines?.length) return !isRequired;
        return lines.every((line) => {
          const hasAmount =
            (line.amount_gross !== null && line.amount_gross !== "") ||
            (line.amount_net !== null && line.amount_net !== "");
          return Boolean(hasAmount && line.currency_code && line.per_type);
        });
      });

    return isRequired ? schema.min(1, t("errors:field_required")) : schema;
  };

  const validateFormValueBySchema = (
    schema: yup.AnySchema,
    path: string,
    formData: QuestionnaireFormValueTypes
  ): string | null => {
    try {
      schema.validateSync(formData[path]);

      return null;
    } catch (error) {
      return error instanceof yup.ValidationError
        ? (error.errors[0] ?? t("errors:field_required"))
        : t("errors:field_required");
    }
  };

  const totalPages = useMemo((): number => {
    return questionnaireForm?.pages.length || 1;
  }, [questionnaireForm]);

  const currentFormPageData = useMemo((): ApiFormViewPage | null => {
    if (!questionnaireForm) return null;
    return questionnaireForm.pages[currentPage - 1];
  }, [questionnaireForm, currentPage]);

  const validationSchema = useMemo((): yup.ObjectSchema<yup.AnyObject> => {
    if (!questionnaireForm) return yup.object().shape({});

    const schema: Record<string, yup.AnySchema> = {};
    const schemaTests: {
      name: string;
      test: (
        formData: QuestionnaireFormValueTypes,
        context: yup.TestContext
      ) => boolean | yup.ValidationError;
    }[] = [];

    questionnaireForm.pages.forEach((page) => {
      page.sections.forEach((section) => {
        section.elements.forEach((element) => {
          if (element.type === ApiFormElementType.text) return;

          const isRequired =
            element.open_answer_settings?.is_required ||
            element.scale_settings?.is_required ||
            element.goals_settings?.is_required ||
            element.select_settings?.is_required ||
            element.employee_nps_settings?.is_required ||
            element.file_settings?.is_required ||
            false;

          const formKey = getFormKey(element);
          if (element.type === ApiFormElementType.select) {
            schema[formKey] = isRequired
              ? yup
                  .array()
                  .of(yup.number().required())
                  .min(1, t("errors:field_required"))
                  .required(t("errors:field_required"))
              : yup.array().notRequired();
          } else if (element.type === ApiFormElementType.scale) {
            schema[formKey] = isRequired
              ? yup
                  .array()
                  .of(yup.number().required())
                  .min(1, t("errors:field_required"))
                  .required(t("errors:field_required"))
              : yup.array().of(yup.number().required()).notRequired();
          } else if (element.type === ApiFormElementType.goals) {
            schema[formKey] =
              isRequired && !element.empty_goals
                ? yup
                    .array()
                    .of(yup.number().required())
                    .min(1, t("errors:field_required"))
                    .required(t("errors:field_required"))
                : yup.array().of(yup.number().required()).notRequired();
          } else if (element.type === ApiFormElementType.open_answer) {
            schema[formKey] = isRequired
              ? yup.string().required(t("errors:field_required"))
              : yup.string().notRequired();
          } else if (element.type === ApiFormElementType.employee_nps) {
            schema[formKey] = isRequired
              ? yup.number().required(t("errors:field_required"))
              : yup.number().notRequired();
          } else if (element.type === ApiFormElementType.file) {
            schema[formKey] = isRequired
              ? yup.array().min(1, t("errors:field_required")).required(t("errors:field_required"))
              : yup.array().notRequired();
          } else if (element.type === ApiFormElementType.write_to_field) {
            if (!element.write_to_field_settings) return;

            const writeToFieldSettings = element.write_to_field_settings;
            const answer = questionnaireForm.answers?.find(
              (answer) => answer.form_element_id === element.id
            );
            const rowsCount =
              writeToFieldSettings.table_mode ===
              ApiFormElementWriteToFieldTableMode.create_multiple
                ? Math.max(answer?.answer_write_to_field?.length ?? 1, 1)
                : 1;
            const isHistoricalTable = !!getWriteToFieldTable(element, writeToFieldSettings)
              ?.is_historical;
            const writeToFieldFields = getWriteToFieldFields(element, writeToFieldSettings);
            const hasEffectiveFromField = writeToFieldFields.some(
              (field) => field.type === ApiFieldTypes.effective_from
            );
            const isTableTarget =
              writeToFieldSettings.target_type === ApiFormElementWriteToFieldTargetType.table;
            const hasMandatoryField = writeToFieldFields.some((field) => field.is_mandatory);

            if (isTableTarget && (writeToFieldSettings.is_required || hasMandatoryField)) {
              schemaTests.push({
                name: `write-to-field-table-${element.id}`,
                test: (formData, context) => {
                  const rowsKey = getWriteToFieldRowsKey(element.id);
                  const rowIndexesValue = formData[rowsKey];
                  const rowIndexes =
                    writeToFieldSettings.table_mode ===
                      ApiFormElementWriteToFieldTableMode.create_multiple &&
                    Array.isArray(rowIndexesValue) &&
                    rowIndexesValue.length > 0
                      ? rowIndexesValue
                      : [0];

                  for (const rowIndex of rowIndexes) {
                    const normalizedRowIndex =
                      writeToFieldSettings.table_mode ===
                      ApiFormElementWriteToFieldTableMode.create_multiple
                        ? Number(rowIndex)
                        : 0;
                    const fieldsWithQuestionnaireSlug = writeToFieldFields.map((field) =>
                      getFieldWithQuestionnaireSlug(
                        field,
                        element.id,
                        field.is_mandatory,
                        normalizedRowIndex
                      )
                    );
                    const rowValidationSlugs = getWriteToFieldValidationSlugs(
                      fieldsWithQuestionnaireSlug
                    );
                    const rowFieldSlugs = fieldsWithQuestionnaireSlug.map((field) => field.slug);
                    const hasFilledRowValue = hasFilledWriteToFieldValue(formData, rowFieldSlugs);
                    const shouldValidateRow = hasFilledWriteToFieldValue(
                      formData,
                      rowValidationSlugs
                    );

                    if (writeToFieldSettings.is_required && !hasFilledRowValue) {
                      const requiredField = fieldsWithQuestionnaireSlug.find(
                        (field) => field.is_mandatory
                      );

                      return context.createError({
                        path: requiredField?.slug ?? rowFieldSlugs[0],
                        message: t("errors:field_required"),
                      });
                    }

                    if (!shouldValidateRow) continue;

                    const fieldsToValidate = fieldsWithQuestionnaireSlug.filter(
                      (field) => field.is_mandatory
                    );

                    for (const field of fieldsToValidate) {
                      if (field.type === ApiFieldTypes.employee_compensation_lines) {
                        const errorMessage = validateFormValueBySchema(
                          getCompensationLinesSchema(true),
                          field.slug,
                          formData
                        );
                        if (errorMessage) {
                          return context.createError({ path: field.slug, message: errorMessage });
                        }
                        continue;
                      }
                      const fieldSchema = getSchemaForField({ ...field, is_mandatory: true });

                      for (const [path, pathSchema] of Object.entries(fieldSchema)) {
                        const errorMessage = validateFormValueBySchema(pathSchema, path, formData);

                        if (errorMessage) {
                          return context.createError({
                            path,
                            message: errorMessage,
                          });
                        }
                      }
                    }

                    if (isHistoricalTable && !hasEffectiveFromField) {
                      const path = getWriteToFieldEffectiveFromSlug(element.id, normalizedRowIndex);
                      const errorMessage = validateFormValueBySchema(
                        yup.date().nullable().required(t("errors:field_required")),
                        path,
                        formData
                      );

                      if (errorMessage) {
                        return context.createError({
                          path,
                          message: errorMessage,
                        });
                      }
                    }
                  }

                  return true;
                },
              });
            }

            Array.from({ length: rowsCount }).forEach((_, rowIndex) => {
              const fieldsWithQuestionnaireSlug = writeToFieldFields.map((field) =>
                getFieldWithQuestionnaireSlug(field, element.id, field.is_mandatory, rowIndex)
              );
              const rowValidationSlugs = getWriteToFieldValidationSlugs(
                fieldsWithQuestionnaireSlug
              );

              if (isHistoricalTable && !hasEffectiveFromField) {
                const effectiveFromSchema = yup
                  .date()
                  .nullable()
                  .required(t("errors:field_required"));

                schema[getWriteToFieldEffectiveFromSlug(element.id, rowIndex)] =
                  writeToFieldSettings.is_required
                    ? effectiveFromSchema
                    : (yup.lazy((_, options) => {
                        const formData = (options.parent ?? {}) as QuestionnaireFormValueTypes;

                        return hasFilledWriteToFieldValue(formData, rowValidationSlugs)
                          ? effectiveFromSchema
                          : yup.date().nullable();
                      }) as unknown as yup.AnySchema);
              }

              fieldsWithQuestionnaireSlug.forEach((field) => {
                if (field.type === ApiFieldTypes.employee_compensation_lines) {
                  schema[field.slug] = getCompensationLinesSchema(
                    writeToFieldSettings.is_required || field.is_mandatory
                  );
                  return;
                }
                Object.assign(
                  schema,
                  getWriteToFieldSchemaForField(
                    field,
                    !isTableTarget && writeToFieldSettings.is_required,
                    rowValidationSlugs
                  )
                );
              });
            });
          }

          if (
            element.scale_settings?.is_comment_enabled ||
            element.select_settings?.is_comment_enabled ||
            element.open_answer_settings?.is_comment_enabled ||
            element.employee_nps_settings?.is_comment_enabled ||
            element.file_settings?.is_comment_enabled ||
            element.write_to_field_settings?.is_comment_enabled
          ) {
            schema[getFormKey(element, true)] = yup.string().notRequired();
          }
        });
      });
    });

    return schemaTests.reduce(
      (objectSchema, schemaTest) =>
        objectSchema.test(schemaTest.name, t("errors:field_required"), function (formData) {
          return schemaTest.test((formData ?? {}) as QuestionnaireFormValueTypes, this);
        }),
      yup.object().shape(schema)
    );
  }, [questionnaireForm]);

  const processAnswers = (): QuestionnaireFormValueTypes => {
    if (!questionnaireForm) return {};

    const formValues: QuestionnaireFormValueTypes = {};

    const allElementsFlat = questionnaireForm.pages.flatMap((page) =>
      page.sections.flatMap((section) => section.elements)
    );

    const answeredWriteToFieldElementIds = new Set(
      questionnaireForm.answers
        .filter((answer) => answer.answer_write_to_field?.length)
        .map((answer) => answer.form_element_id)
    );

    allElementsFlat.forEach((element) => {
      const settings = element.write_to_field_settings;
      if (
        element.type !== ApiFormElementType.write_to_field ||
        !settings ||
        settings.target_type !== ApiFormElementWriteToFieldTargetType.table ||
        settings.table_mode !== ApiFormElementWriteToFieldTableMode.create_new ||
        answeredWriteToFieldElementIds.has(element.id) ||
        getWriteToFieldTable(element, settings)?.table_type === ApiTableTypes.custom
      ) {
        return;
      }

      getWriteToFieldFields(element, settings).forEach((field) => {
        if (field.type === ApiFieldTypes.employee_compensation_lines) {
          const lines = targetEmployee?.employee_compensation?.lines;
          if (lines?.length) {
            const fieldWithQuestionnaireSlug = getFieldWithQuestionnaireSlug(field, element.id);
            formValues[fieldWithQuestionnaireSlug.slug] = lines.map((line) => ({
              id: line.id,
              compensation_component_id: line.compensation_component_id,
              amount_gross: line.amount_gross,
              amount_net: line.amount_net,
              currency_code: line.currency_code,
              per_type: line.per_type,
              sort: line.sort,
              description: line.description,
              rate_type: line.rate_type ?? null,
              percent: line.percent ?? null,
            }));
          }
          return;
        }
        const value = getTargetEmployeeFieldValue(
          field,
          targetEmployee,
          employeeMe?.organization.currency
        );
        if (!value) return;

        const fieldWithQuestionnaireSlug = getFieldWithQuestionnaireSlug(field, element.id);
        Object.assign(
          formValues,
          getValueForFieldFromValueObject(fieldWithQuestionnaireSlug, value)
        );
      });
    });

    questionnaireForm.answers.forEach((answer) => {
      const elementId = answer.form_element_id;
      const element = allElementsFlat.find((el) => {
        if (el.id !== elementId) return false;
        if (el.type === ApiFormElementType.goals && answer.reference_id) {
          return el.reference_id === answer.reference_id;
        }
        return true;
      });
      if (!element) return;

      const fieldKey = getFormKey(element);

      if (element.type === ApiFormElementType.select) {
        if (answer.answer_select_option_ids?.length) {
          formValues[fieldKey] = answer.answer_select_option_ids;
        }
      } else if (element.type === ApiFormElementType.scale) {
        if (answer.answer_scale_option_ids?.length) {
          formValues[fieldKey] = answer.answer_scale_option_ids;
        }
      } else if (element.type === ApiFormElementType.goals) {
        if (answer.answer_scale_option_ids?.length) {
          formValues[fieldKey] = answer.answer_scale_option_ids;
        }
      } else if (element.type === ApiFormElementType.open_answer) {
        if (answer.answer_text) {
          formValues[fieldKey] = answer.answer_text;
        }
      } else if (element.type === ApiFormElementType.employee_nps) {
        if (answer.answer_int !== undefined) {
          formValues[fieldKey] = answer.answer_int;
        }
      } else if (element.type === ApiFormElementType.file) {
        formValues[fieldKey] = answer.answer_files ?? [];
      } else if (element.type === ApiFormElementType.write_to_field) {
        if (element.write_to_field_settings && answer.answer_write_to_field?.length) {
          const rows = answer.answer_write_to_field;
          const isCreateMultiple =
            element.write_to_field_settings.table_mode ===
            ApiFormElementWriteToFieldTableMode.create_multiple;

          if (isCreateMultiple) {
            formValues[getWriteToFieldRowsKey(element.id)] = rows.map((_, rowIndex) => rowIndex);
          }

          rows.forEach((row, rowIndex) => {
            if (row.date_effective_from) {
              const normalizedRowIndex = isCreateMultiple ? rowIndex : 0;
              const effectiveFromField = getWriteToFieldFields(
                element,
                element.write_to_field_settings!
              ).find((field) => field.type === ApiFieldTypes.effective_from);
              const dateFieldSlug = effectiveFromField
                ? getFieldWithQuestionnaireSlug(
                    effectiveFromField,
                    element.id,
                    effectiveFromField.is_mandatory,
                    normalizedRowIndex
                  ).slug
                : getWriteToFieldEffectiveFromSlug(element.id, normalizedRowIndex);

              formValues[dateFieldSlug] = new Date(row.date_effective_from);
            }

            getWriteToFieldFields(element, element.write_to_field_settings!).forEach((field) => {
              const cell = row.cells?.find(
                (cell) => cell.field_id === field.id && cell.field_type === field.type
              );
              if (!cell) return;

              const fieldWithQuestionnaireSlug = getFieldWithQuestionnaireSlug(
                field,
                element.id,
                field.is_mandatory,
                isCreateMultiple ? rowIndex : 0
              );

              if (field.type === ApiFieldTypes.employee_compensation_lines) {
                formValues[fieldWithQuestionnaireSlug.slug] = cell.value_compensation_lines ?? [];
                return;
              }

              Object.assign(
                formValues,
                getValueForFieldFromValueObject(fieldWithQuestionnaireSlug, cell.value)
              );
            });
          });
        }
      }
      if (answer.comment) {
        formValues[getFormKey(element, true)] = answer.comment;
      }
    });

    return formValues;
  };

  const formValuesToAnswers = (formData: QuestionnaireFormValueTypes): ApiFormResultAnswer[] => {
    if (!questionnaireForm) return [];

    const answers: ApiFormResultAnswer[] = [];

    questionnaireForm.pages.forEach((page) => {
      page.sections.forEach((section) => {
        section.elements.forEach((element) => {
          if (element.type === ApiFormElementType.text) return;

          const elementId = element.id;
          const answer: ApiFormResultAnswer = {
            form_element_id: elementId,
          };

          if (element.type === ApiFormElementType.goals && element.reference_id) {
            answer.reference_id = element.reference_id;
          }

          const commentValue = formData[getFormKey(element, true)];
          if (typeof commentValue === "string" && commentValue.trim()) {
            answer.comment = commentValue;
          }

          const fieldValue = formData[getFormKey(element)];

          switch (element.type) {
            case ApiFormElementType.select:
              if (fieldValue !== undefined) {
                answer.answer_select_option_ids = Array.isArray(fieldValue)
                  ? fieldValue.map(Number)
                  : [Number(fieldValue)];
              }
              break;

            case ApiFormElementType.scale:
            case ApiFormElementType.goals:
              if (fieldValue !== undefined) {
                answer.answer_scale_option_ids = Array.isArray(fieldValue)
                  ? fieldValue.map(Number)
                  : [Number(fieldValue)];
              }
              break;

            case ApiFormElementType.open_answer:
              if (typeof fieldValue === "string" && fieldValue.trim()) {
                answer.answer_text = fieldValue;
              }
              break;

            case ApiFormElementType.employee_nps:
              if (fieldValue !== undefined) {
                answer.answer_int = Number(fieldValue);
              }
              break;

            case ApiFormElementType.file:
              answer.answer_file_uuids = Array.isArray(fieldValue)
                ? fieldValue.flatMap((file) =>
                    typeof file === "object" && file !== null && "uuid" in file
                      ? [String(file.uuid)]
                      : []
                  )
                : [];
              break;

            case ApiFormElementType.write_to_field:
              if (element.write_to_field_settings) {
                const isCreateMultiple =
                  element.write_to_field_settings.table_mode ===
                  ApiFormElementWriteToFieldTableMode.create_multiple;
                const rowIndexesValue = formData[getWriteToFieldRowsKey(element.id)];
                const rowIndexes =
                  isCreateMultiple && Array.isArray(rowIndexesValue) && rowIndexesValue.length > 0
                    ? rowIndexesValue
                    : [0];
                const isHistoricalTable = !!getWriteToFieldTable(
                  element,
                  element.write_to_field_settings
                )?.is_historical;
                const rows = rowIndexes.map((rowIndex) => {
                  const normalizedRowIndex = isCreateMultiple ? Number(rowIndex) : 0;
                  const writeToFieldFields = getWriteToFieldFields(
                    element,
                    element.write_to_field_settings!
                  );
                  const cells = writeToFieldFields.map((field) => {
                    const fieldWithQuestionnaireSlug = getFieldWithQuestionnaireSlug(
                      field,
                      element.id,
                      field.is_mandatory,
                      normalizedRowIndex
                    );
                    if (field.type === ApiFieldTypes.employee_compensation_lines) {
                      const lines = formData[fieldWithQuestionnaireSlug.slug] as
                        ApiFormResultCompensationLine[] | undefined;
                      return {
                        field_id: field.id,
                        field_type: field.type,
                        value: {},
                        value_compensation_lines: normalizeCompensationLinesForSubmit(lines ?? []),
                      };
                    }
                    return {
                      field_id: field.id,
                      field_type: field.type,
                      value: transformWriteToFieldFormDataToValue(
                        fieldWithQuestionnaireSlug,
                        formData
                      ),
                    };
                  });

                  const effectiveFromField = writeToFieldFields.find(
                    (field) => field.type === ApiFieldTypes.effective_from
                  );
                  const effectiveFromFieldValue = effectiveFromField
                    ? formData[
                        getFieldWithQuestionnaireSlug(
                          effectiveFromField,
                          element.id,
                          effectiveFromField.is_mandatory,
                          normalizedRowIndex
                        ).slug
                      ]
                    : undefined;

                  return {
                    cells,
                    target_row_id: null,
                    date_effective_from: isHistoricalTable
                      ? (((effectiveFromField
                          ? effectiveFromFieldValue
                          : formData[
                              getWriteToFieldEffectiveFromSlug(element.id, normalizedRowIndex)
                            ]) as Date | null | undefined) ??
                        (effectiveFromField ? new Date() : null))
                      : null,
                  };
                });

                if (rows.some((row) => row.cells.length > 0)) {
                  answer.answer_write_to_field = rows;
                }
              }
              break;
          }

          if (
            answer.comment ||
            answer.answer_text ||
            answer.answer_int !== undefined ||
            answer.answer_scale_option_ids?.length ||
            answer.answer_select_option_ids?.length ||
            answer.answer_write_to_field?.length ||
            answer.answer_file_uuids !== undefined
          ) {
            answers.push(answer);
          }
        });
      });
    });

    return answers;
  };

  const methods = useForm<QuestionnaireFormValueTypes>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, isDirty, dirtyFields },
    getValues,
  } = methods;

  useEffect(() => {
    if (!questionnaireForm) return;
    reset(processAnswers());
  }, [questionnaireForm, reset]);

  const handleSaveDraft = async (): Promise<void> => {
    setIsSaving(true);
    const formData = getValues();
    const answersPayload = formValuesToAnswers(formData);
    try {
      await saveDraft.mutateAsync({
        employeeId: employeeMe?.id || 0,
        questionnaireId: Number(id),
        answers: answersPayload,
      });
      if (questionnaireType.type == ApiFormType.survey) {
        analyticsService.trackEvent(analyticEvents.surveys.responseDraftSaved, {
          [analyticProperties.id]: id,
        });
      }
      if (questionnaireType.type == ApiFormType.performanceReview) {
        analyticsService.trackEvent(analyticEvents.performanceReviews.responseDraftSaved, {
          [analyticProperties.id]: id,
        });
      }
    } catch {
      //
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const handleVisibilityChange = (): void => {
      const isVisible = !document.hidden;
      setIsDocumentVisible(isVisible);

      if (!isVisible && isDirty) {
        handleSaveDraft();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    const setupAutoSave = (): void => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }

      autoSaveTimerRef.current = setInterval(() => {
        if (isDocumentVisible && isDirty) {
          handleSaveDraft();
        }
      }, 30000);
    };

    setupAutoSave();

    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [getValues, isDirty, saveDraft, employeeMe?.id, id]);

  const scrollToFirstError = (): void => {
    const firstErrorElement = document.querySelector("[data-error='true']");
    if (firstErrorElement) {
      firstErrorElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const onSubmit = handleSubmit(
    async (formData) => {
      const answersPayload = formValuesToAnswers(formData);
      setIsSaving(true);
      try {
        await submitQuestionnaire.mutateAsync({
          employeeId: employeeMe?.id || 0,
          questionnaireId: Number(id),
          answers: answersPayload,
        });
        const changedCurrencies = [
          ...new Set(
            Object.keys(dirtyFields)
              .filter(
                (name) =>
                  name.endsWith("_currency") ||
                  [
                    ApiFieldTypes.employee_compensation_currency,
                    ApiFieldTypes.employee_additional_compensation_currency,
                  ].some((type) => name.includes(`_${type}_`))
              )
              .flatMap<unknown>((name) => formData[name])
              .filter((code): code is string => typeof code === "string" && code !== "")
          ),
        ];
        if (changedCurrencies.length > 0) {
          addRecent([{ type: ApiRecentKey.currency, values: changedCurrencies }]);
        }
        onAfterSubmit?.();
      } catch (error) {
        console.error("Error submitting questionnaire:", error);
      } finally {
        setIsSaving(false);
      }
    },
    () => {
      setTimeout(scrollToFirstError, 100);
    }
  );

  const validateCurrentPage = (): Promise<boolean> => {
    if (!currentFormPageData || !questionnaireForm) return Promise.resolve(true);

    const currentPageFields: string[] = [];
    currentFormPageData.sections.forEach((section) => {
      section.elements.forEach((element) => {
        if (element.type !== ApiFormElementType.text) {
          currentPageFields.push(getFormKey(element));
        }
      });
    });

    return methods.trigger(currentPageFields);
  };

  const handleNextPage = async (): Promise<void> => {
    if (currentPage < totalPages) {
      const isValid = await validateCurrentPage();
      if (isValid) {
        await handleSaveDraft();
        setCurrentPage(currentPage + 1);
      } else {
        setTimeout(scrollToFirstError, 100);
      }
    }
  };

  const handlePreviousPage = (): void => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return {
    methods,
    questionnaireForm,
    currentPage,
    totalPages,
    isSaving,
    isSubmitting: submitQuestionnaire.isPending,
    submitQuestionnaire: onSubmit,
    handleNextPage,
    handlePreviousPage,
    handleSaveDraft,
    questionnaireType,
    errors,
  };
};
